import React, { useState, useRef, useEffect, useCallback } from "react";

interface InlineTimePickerProps {
  value: Date | null;
  onChange: (date: Date) => void;
  onSave?: () => void;
  onCancel?: () => void;
  format: "12h" | "24h";
  placeholder?: string;
  displayClassName?: string;
  isSimple?: boolean;
  formatDisplay?: (date: Date | null) => string;
}

const InlineTimePicker: React.FC<InlineTimePickerProps> = ({
  value,
  onChange,
  onSave,
  onCancel,
  format,
  placeholder = "Click to select time",
  displayClassName = "",
  isSimple = false,
  formatDisplay,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const getTimeState = useCallback(
    (date: Date | null) => {
      if (!date) {
        return {
          hours: format === "12h" ? 12 : 0,
          minutes: 0,
          period: "AM" as "AM" | "PM",
        };
      }

      const hours24 = date.getHours();
      const minutes = Math.floor(date.getMinutes() / 15) * 15;

      if (format === "12h") {
        const hours12 =
          hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
        const period = hours24 >= 12 ? "PM" : "AM";
        return { hours: hours12, minutes, period };
      }

      return { hours: hours24, minutes, period: "AM" as "AM" | "PM" };
    },
    [format],
  );

  const [timeState, setTimeState] = useState(getTimeState(value));

  useEffect(() => {
    setTimeState(getTimeState(value));
  }, [value, format, getTimeState]);

  const formatTime = (date: Date | null) => {
    if (!date) return "";
    // Use custom formatter if provided
    if (formatDisplay) {
      return formatDisplay(date);
    }

    const hours = date.getHours();
    const minutes = date.getMinutes();

    if (format === "12h") {
      var formattedHours = hours;
      var suffix = "AM";

      if (hours >= 12) {
        suffix = "PM";

        if (hours > 12) {
          formattedHours -= 12;
        }
      }
      return `${formattedHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${suffix}`;
    }

    // Default: simple xx:yy format for display
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  };

  const handleTimeChange = (newState: typeof timeState) => {
    setTimeState(newState);

    const date = value ? new Date(value) : new Date();

    let hours24 = newState.hours;
    if (format === "12h") {
      if (newState.period === "PM" && newState.hours !== 12) {
        hours24 = newState.hours + 12;
      } else if (newState.period === "AM" && newState.hours === 12) {
        hours24 = 0;
      }
    }

    date.setHours(hours24);
    date.setMinutes(newState.minutes);
    date.setSeconds(0);
    date.setMilliseconds(0);

    onChange(date);

    if (isSimple) {
      onChange(date);
    }
  };

  const handleSave = useCallback(() => {
    onSave?.();
    setIsEditing(false);
  }, [onSave]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    setTimeState(getTimeState(value));
    setIsEditing(false);
  }, [getTimeState, onCancel, value]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside the container
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }

      // Close based on mode
      if (isSimple) {
        setIsOpen(false);
      } else {
        handleCancel();
      }
    };

    if ((isSimple && isOpen) || (!isSimple && isEditing)) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, isSimple, isOpen, handleCancel]);

  const hours =
    format === "12h"
      ? Array.from({ length: 12 }, (_, i) => i + 1)
      : Array.from({ length: 24 }, (_, i) => i);

  const minutes = [0, 15, 30, 45];

  // Simple mode - just show the controls, no save/cancel needed
  if (isSimple) {
    if (!isOpen) {
      return (
        <div
          ref={triggerRef}
          onClick={() => setIsOpen(true)}
          className={`cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors ${displayClassName}`}
        >
          {formatTime(value) || (
            <span className="text-gray-400 italic">{placeholder}</span>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1" ref={containerRef}>
        {/* Hours dropdown */}
        <select
          value={timeState.hours}
          onChange={(e) =>
            handleTimeChange({ ...timeState, hours: parseInt(e.target.value) })
          }
          className={`p-2 rounded border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-info ${displayClassName}`}
        >
          {hours.map((h) => (
            <option key={h} value={h}>
              {format === "24h" ? h.toString().padStart(2, "0") : h}
            </option>
          ))}
        </select>

        <span className="text-gray-500">:</span>

        {/* Minutes dropdown */}
        <select
          value={timeState.minutes}
          onChange={(e) =>
            handleTimeChange({
              ...timeState,
              minutes: parseInt(e.target.value),
            })
          }
          className={`p-2 rounded border border-gray-300 bg-white mr-1 focus:outline-none focus:ring-2 focus:ring-info ${displayClassName}`}
        >
          {minutes.map((m) => (
            <option key={m} value={m}>
              {m.toString().padStart(2, "0")}
            </option>
          ))}
        </select>

        {/* AM/PM toggle for 12-hour format */}
        {format === "12h" && (
          <div className="flex border border-gray-300 rounded overflow-hidden">
            <button
              type="button"
              onClick={() => handleTimeChange({ ...timeState, period: "AM" })}
              className={`px-2 py-1 text-sm font-medium transition-colors ${
                timeState.period === "AM"
                  ? "bg-info text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              AM
            </button>
            <button
              type="button"
              onClick={() => handleTimeChange({ ...timeState, period: "PM" })}
              className={`px-2 py-1 text-sm font-medium transition-colors ${
                timeState.period === "PM"
                  ? "bg-info text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              PM
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!isEditing) {
    return (
      <div
        ref={triggerRef}
        onClick={() => setIsEditing(true)}
        className={`cursor-pointer hover:bg-secondary-light p-2 rounded transition-colors ${displayClassName}`}
      >
        {formatTime(value) || (
          <span className="text-gray-400 italic">{placeholder}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" ref={containerRef}>
      {/* Hours dropdown */}
      <select
        value={timeState.hours}
        onChange={(e) =>
          handleTimeChange({ ...timeState, hours: parseInt(e.target.value) })
        }
        className="p-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-info"
      >
        {hours.map((h) => (
          <option key={h} value={h}>
            {format === "24h" ? h.toString().padStart(2, "0") : h}
          </option>
        ))}
      </select>

      <span className="text-gray-500">:</span>

      {/* Minutes dropdown */}
      <select
        value={timeState.minutes}
        onChange={(e) =>
          handleTimeChange({ ...timeState, minutes: parseInt(e.target.value) })
        }
        className="p-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-info"
      >
        {minutes.map((m) => (
          <option key={m} value={m}>
            {m.toString().padStart(2, "0")}
          </option>
        ))}
      </select>

      {/* AM/PM toggle for 12-hour format */}
      {format === "12h" && (
        <div className="flex border border-gray-300 rounded overflow-hidden">
          <button
            type="button"
            onClick={() => handleTimeChange({ ...timeState, period: "AM" })}
            className={`px-2 py-1 text-sm font-medium transition-colors ${
              timeState.period === "AM"
                ? "bg-info text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            AM
          </button>
          <button
            type="button"
            onClick={() => handleTimeChange({ ...timeState, period: "PM" })}
            className={`px-2 py-1 text-sm font-medium transition-colors ${
              timeState.period === "PM"
                ? "bg-info text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            PM
          </button>
        </div>
      )}

      {/* Save/Cancel buttons */}
      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          handleSave();
        }}
        className="p-2 rounded bg-success text-white hover:bg-success-dark transition-colors"
        aria-label="Save"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </button>
      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          handleCancel();
        }}
        className="p-2 rounded bg-gray-300 text-gray-700 hover:bg-gray-400 transition-colors"
        aria-label="Cancel"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
};

export { InlineTimePicker };
